// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Crea il context
const AuthContext = createContext();

// Hook personalizzato per usare il context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di un AuthProvider');
  }
  return context;
};

// Provider del context
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listener per i cambiamenti dello stato di autenticazione
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Carica i dati dell'utente da Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentUser(user);
            setUserRole(userData.role || 'user');
            setUserData(userData);
          } else {
            // Se il documento utente non esiste, usa dati di base
            console.warn('Documento utente non trovato per UID:', user.uid);
            setCurrentUser(user);
            setUserRole('user');
            setUserData({
              email: user.email,
              role: 'user'
            });
          }
        } catch (error) {
          console.error('Errore nel caricamento dati utente:', error);
          setCurrentUser(user);
          setUserRole('user');
          setUserData({
            email: user.email,
            role: 'user'
          });
        }
      } else {
        // Utente non autenticato
        setCurrentUser(null);
        setUserRole(null);
        setUserData(null);
      }
      setLoading(false);
    });

    // Cleanup del listener
    return unsubscribe;
  }, []);

  // Funzione per ricaricare i dati utente (utile dopo aggiornamenti)
  const refreshUserData = async () => {
    if (currentUser) {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const newUserData = userDoc.data();
          setUserRole(newUserData.role || 'user');
          setUserData(newUserData);
        }
      } catch (error) {
        console.error('Errore nel refresh dati utente:', error);
      }
    }
  };

  // Funzioni di utilità
  const isAdmin = () => userRole === 'admin';
  const isResponsabile = () => userRole === 'responsabile';
  const isUser = () => userRole === 'user';
  const hasElevatedPrivileges = () => userRole === 'admin' || userRole === 'responsabile';

  // Valore del context
  const value = {
    currentUser,
    userRole,
    userData,
    loading,
    isAdmin,
    isResponsabile,
    isUser,
    hasElevatedPrivileges,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;